import { ListWorkOrdersResult } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  WorkOrderComplementStatus,
  workorderComplementLabel,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { Modal, Button, Table, ModalProps, AutoComplete } from 'antd';
import { Send } from 'react-feather';
import { wppTextFormat } from '../../Details/WorkOrderFooter/ComplementWhatsappButton/options';
import { useCustomerReduced } from '@/core/domain/people/Customers/Customers.hooks';
import { useState } from 'react';
import { CustomerReduced } from '@/core/domain/people/Customers/Customers.types';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { cellphoneMask } from '@/utils/mask/commonMasks';
import { applyMask, removeMask } from '@/utils/mask';
import { useCustomerContactsMutation } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { CustomerContact } from '@/core/domain/people/CustomerContacts/CustomerContacts.types';
import { useReportTokenGenerate } from '@/core/domain/reports/reportToken/reportToken.hooks';
import { ReportTokenType } from '@/core/domain/reports/reportToken/reportToken.types';
import { useWindowSize } from '@/hooks';

export interface CustomerWorkorder {
  customer: { id: number; name: string };
  workorders: ListWorkOrdersResult[];
}

interface CustomerState {
  customer?: CustomerReduced;
  contacts?: CustomerContact[];
}

interface Props extends ModalProps {
  customerWorkorders: CustomerWorkorder[];
}

const WorkOrdersByWhatsappModal: React.FC<Props> = ({
  customerWorkorders,
  ...props
}) => {
  const customerDetail = useCustomerReduced();
  const customerContacts = useCustomerContactsMutation();
  const reportTokenGenerate = useReportTokenGenerate();

  const { isMobile } = useWindowSize();

  const [customerWorkorder, setCustomerWorkorder] =
    useState<CustomerWorkorder>();
  const [customer, setCustomer] = useState<CustomerState | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string>();

  async function sendCustomerWhatsapp() {
    const statusIcons = {
      [WorkOrderComplementStatus.Authorized]: `🟠`,
      [WorkOrderComplementStatus.Cancelled]: `🔴`,
      [WorkOrderComplementStatus.Closed]: `🟢`,
      [WorkOrderComplementStatus.Open]: `🔵`,
    };

    const complements = await Promise.all(
      customerWorkorder?.workorders.map(async (item) => {
        const arToken = await reportTokenGenerate.mutateAsync({
          params: JSON.stringify({
            workOrderId: item.workOrderId,
            complementNumber: item.complementNumber,
            includeCancelledParts: false,
          }),
          reportType: ReportTokenType.ADMINISTRATIVE,
        });

        return `*${statusIcons[item.status]} ${item.complementIdentifier} - ${
          item.licensePlate || 'Sem Placa'
        } - ${workorderComplementLabel[item.status]}*\n${
          window.location.origin
        }/ar/${arToken.token}`;
      }) || [],
    );

    const textToSend = wppTextFormat({
      wppText: `*Orçamentos:*\n\n${complements.join(`\n\n`)}`,
    });

    const urlQueryString = new URLSearchParams({
      phone: `55${phoneNumber}`,
      text: textToSend,
    }).toString();

    if (isMobile) {
      window.open(
        `https://wa.me/55${phoneNumber}?text=${encodeURIComponent(textToSend)}`,
        '_blank',
      );
    } else {
      window.open(`https://web.whatsapp.com/send?${urlQueryString}`, '_blank');
    }
  }

  async function getCustomerData(customerId: number) {
    try {
      const customer = await customerDetail.mutateAsync(customerId);
      const contacts = await customerContacts.mutateAsync(customerId);

      setCustomer({
        customer,
        contacts,
      });
    } catch {}
  }

  return (
    <>
      <Modal
        {...props}
        title="Selecione o cliente e o contato de envio"
        footer={false}
      >
        <Table
          dataSource={customerWorkorders}
          columns={[
            { title: 'Cliente', dataIndex: ['customer', 'name'] },
            { title: 'Orçamentos', render: (_, row) => row.workorders.length },
            {
              render: (_, row) => (
                <Button
                  shape="round"
                  type="primary"
                  onClick={() => {
                    setCustomerWorkorder(row);
                    getCustomerData(row.customer.id);
                  }}
                  loading={
                    customerDetail.isLoading &&
                    customerDetail.variables === row.customer.id &&
                    !customerDetail.data
                  }
                  style={{ padding: 0, width: 32 }}
                >
                  {customerDetail.isLoading &&
                  customerDetail.variables === row.customer.id ? null : (
                    <Send size={14} style={{ margin: 0 }} />
                  )}
                </Button>
              ),
            },
          ]}
          pagination={false}
        />
      </Modal>

      <Modal
        open={!!customer?.customer}
        title={customerWorkorder?.customer.name}
        onCancel={() => {
          setCustomer(undefined);
          setCustomerWorkorder(undefined);
          setPhoneNumber(undefined);
        }}
        onOk={sendCustomerWhatsapp}
        okText="Enviar"
        okButtonProps={{ disabled: !phoneNumber }}
      >
        <p>Selecione ou preencha o número do whatsapp a ser enviado</p>

        <AutoComplete
          style={{ width: '100%' }}
          placeholder="Informe o número do whatsapp"
          options={[
            ...(phoneNumber
              ? [
                  {
                    value: applyMask(removeMask(phoneNumber), cellphoneMask),
                    label: applyMask(removeMask(phoneNumber), cellphoneMask),
                  },
                ]
              : []),

            ...(customer?.contacts
              ?.flatMap((contact) =>
                contact.detailList.filter(
                  (item) =>
                    item.contactType === ContactType.WhatsApp && item.active,
                ),
              )
              .map((item) => ({
                value: applyMask(removeMask(item.value), cellphoneMask),
                label: applyMask(removeMask(item.value), cellphoneMask),
              }))
              .filter(
                (item) =>
                  item.value !==
                  (phoneNumber
                    ? applyMask(removeMask(phoneNumber), cellphoneMask)
                    : undefined),
              ) || []),
          ]}
          value={
            phoneNumber
              ? applyMask(removeMask(phoneNumber), cellphoneMask)
              : undefined
          }
          onChange={(value) => setPhoneNumber(removeMask(value))}
        />
      </Modal>
    </>
  );
};

export default WorkOrdersByWhatsappModal;
